/**
 * Child entrypoint preloading the Invited App Shortcuts recommendations
 * query alongside the parent legacy-nav entrypoint. Cohort gating
 * (kill-switch + experiment) lives in the parent — `.entrypoint.tsx`
 * files cannot import feature-gate APIs. Mirrors the PUMA-2000 GSN
 * child entrypoint, with its own package-scoped Relay artifact.
 */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import parameters, {
	type GlobalShortcutsInvitedAppShortcutsRecommendationsQuery,
} from './__generated__/GlobalShortcutsInvitedAppShortcutsRecommendationsQuery.graphql.ts';
import type { InvitedAppShortcutsRecommendationsEntrypointProps } from './invitedAppShortcutsRecommendations.entrypoint.types.tsx';

type Component = EntryPointComponent<
	{ recommendations: GlobalShortcutsInvitedAppShortcutsRecommendationsQuery },
	{},
	InvitedAppShortcutsRecommendationsEntrypointProps,
	{}
>;

export const invitedAppShortcutsRecommendationsEntryPoint: EntryPoint<
	Component,
	{ cloudId: string }
> = createEntryPoint({
	root: JSResourceForUserVisible<Component>(
		/* webpackChunkName: "global-shortcuts-invited-app-shortcuts-recommendations-entrypoint" */ './GlobalShortcutsInvitedAppShortcutsRecommendations.tsx',
	),
	getPreloadProps: ({ cloudId }: { cloudId: string }) => ({
		queries: {
			recommendations: {
				parameters,
				// `rerank: [{ entityId: 'confluence' }]` biases server-side ranking
				// toward the cross-product target (Jira -> Confluence). Confluence's
				// equivalent surface uses `[{ entityId: 'jira' }]`. This is an
				// intentional deviation from the cross-join-nudges README's
				// "omit rerank in SSR preloaders" guidance, because Invited App
				// Shortcuts always wants the SSR-preloaded cache reranked for the
				// cross-product bias.
				variables: {
					tenantId: cloudId,
					product: 'jira',
					rerank: [{ entityId: 'confluence' }],
				},
				options: { fetchPolicy: 'store-or-network' as const },
			},
		},
	}),
});
